import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Paper,
    Box,
    Grid,
    useTheme,
    Avatar,
    Chip
} from '@mui/material';
import { Helmet } from 'react-helmet-async';

interface Person {
    nama: string;
    foto: string;
    job: string;
    status: string;
}

const About: React.FC = () => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const headingColor = isDarkMode ? '#90caf9' : '#1a237e';
    const [people, setPeople] = useState<Person[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchTentangData = async () => {
            try {
                const response = await fetch('/api/bookings?action=tentang');
                if (response.ok) {
                    const data = await response.json();
                    setPeople(data);
                }
            } catch (error) {
                console.error('Error fetching tentang data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchTentangData();
    }, []);

    return (
        <>
            <Helmet>
                <title>Tentang RaketerS - Platform Inovatif Badminton Bandung</title>
                <meta name="description" content="RaketerS adalah platform yang dikembangkan dengan visi untuk mempermudah akses ke olahraga badminton di Bandung melalui teknologi. Bergabunglah dengan kami untuk pengalaman badminton yang lebih baik dan komunitas yang solid." />
                <meta name="keywords" content="RaketerS, mabar badminton Bandung, komunitas badminton, main bareng badminton, GOR 150 Antapani, GOR KONI Bandung" />
                <link rel="canonical" href="https://raketers.com/tentang-raketers" />
                <meta property="og:title" content="Tentang RaketerS - Platform Inovatif Badminton Bandung" />
                <meta property="og:description" content="Pelajari lebih lanjut tentang RaketerS, platform yang mempermudah akses ke badminton di Bandung dengan teknologi. Temukan orang-orang di balik RaketerS dan bergabunglah dengan komunitas kami." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://raketers.com/tentang-raketers" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Tentang RaketerS - Platform Inovatif Badminton Bandung" />
                <meta name="twitter:description" content="Pelajari lebih lanjut tentang RaketerS, platform yang mempermudah akses ke badminton di Bandung dengan teknologi." />
                {!loading && people.length > 0 && (
                    <script type="application/ld+json">
                        {JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "Organization",
                            "name": "RaketerS",
                            "description": "Platform Inovatif Badminton di Bandung",
                            "url": "https://raketers.com",
                            "foundingLocation": {
                                "@type": "City",
                                "name": "Bandung",
                                "addressCountry": "ID"
                            },
                            "member": people.map(person => ({
                                "@type": "Person",
                                "name": person.nama,
                                "jobTitle": person.job || "Anggota Komunitas",
                                "image": person.foto || undefined
                            }))
                        })}
                    </script>
                )}
            </Helmet>

            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Typography variant="h2" component="h1" gutterBottom align="center" sx={{ fontWeight: 'bold', color: headingColor }}>
                    Tentang RaketerS
                </Typography>
                
                <Box sx={{ my: 4 }}>
                    <Typography variant="h5" gutterBottom sx={{ color: headingColor }}>
                        Platform Inovatif Badminton Bandung
                    </Typography>
                    <Typography paragraph>
                        RaketerS adalah platform yang menghubungkan pemain badminton dengan teman main dan lapangan badminton berkualitas. Kami menyediakan platform booking mabar yang mudah, cepat, dan terpercaya untuk memenuhi kebutuhan komunitas badminton di Bandung, diperkuat dengan teknologi inovatif.
                    </Typography>
                    <Typography paragraph>
                        Didirikan dengan visi untuk membuat badminton lebih mudah diakses oleh semua orang, RaketerS bukan murni komunitas nonprofit, melainkan sebuah platform yang dikembangkan dengan pendekatan komersial untuk mempermudah orang-orang mendapatkan akses ke olahraga badminton dan diperkuat dengan teknologi. Kami telah berkembang menjadi platform yang solid dengan ribuan pengguna aktif, mempercayai bahwa olahraga bukan hanya tentang kompetisi, tapi juga tentang membangun persahabatan dan kesehatan bersama. Sebagai efek samping positif dari platform ini, terbentuklah komunitas badminton yang kuat, dan kami tetap berkomitmen untuk mewadahi serta mendukung pertumbuhan komunitas tersebut.
                    </Typography>
                    <Typography paragraph>
                        Tim RaketerS terdiri dari para pecinta badminton yang berdedikasi untuk mengembangkan ekosistem badminton di Bandung. Dengan pengalaman bertahun-tahun di dunia badminton, tim kami terus berinovasi untuk memberikan pengalaman terbaik bagi para pemain dari berbagai level kemampuan.
                    </Typography>
                </Box>

                <Grid container spacing={4} sx={{ my: 4 }}>
                    <Grid item xs={12} md={4}>
                        <Paper elevation={3} sx={{ 
                            p: 3, 
                            height: '100%',
                            background: (theme) => theme.palette.mode === 'dark' ? '#0a1626' : '#fff',
                            border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(99,170,255,0.22)' : undefined
                        }}>
                            <Typography variant="h6" gutterBottom sx={{ color: headingColor }}>
                                Mabar Badminton Mudah
                            </Typography>
                            <Typography>
                                Dengan RaketerS, mencari teman mabar badminton di Bandung menjadi lebih mudah. Cukup pilih lapangan, pilih waktu, dan bergabung dengan sesi mabar yang sudah ada. Tidak perlu repot mencari teman main.
                            </Typography>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Paper elevation={3} sx={{ 
                            p: 3, 
                            height: '100%',
                            background: (theme) => theme.palette.mode === 'dark' ? '#0a1626' : '#fff',
                            border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(99,170,255,0.22)' : undefined
                        }}>
                            <Typography variant="h6" gutterBottom sx={{ color: headingColor }}>
                                Komunitas Badminton Bandung
                            </Typography>
                            <Typography>
                                Bergabunglah dengan komunitas badminton terbesar di Bandung. Temukan teman main dengan level yang sesuai, berbagi pengalaman, dan tingkatkan skill badminton Anda bersama.
                            </Typography>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Paper elevation={3} sx={{ 
                            p: 3, 
                            height: '100%',
                            background: (theme) => theme.palette.mode === 'dark' ? '#0a1626' : '#fff',
                            border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(99,170,255,0.22)' : undefined
                        }}>
                            <Typography variant="h6" gutterBottom sx={{ color: headingColor }}>
                                Coaching Clinic Badminton
                            </Typography>
                            <Typography>
                                Tingkatkan skill badminton Anda dengan bergabung di coaching clinic kami. Pelatih profesional akan membantu Anda menguasai teknik dasar hingga lanjutan.
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                <Box sx={{ my: 4 }}>
                    <Typography variant="h5" gutterBottom sx={{ color: headingColor }}>
                        Mengapa Bergabung dengan RaketerS?
                    </Typography>
                    <Typography paragraph>
                        Sebagai komunitas mabar badminton terpercaya di Bandung, RaketerS menawarkan berbagai keunggulan:
                    </Typography>
                    <ul>
                        <li>Booking mabar badminton 24/7</li>
                        <li>Komunitas badminton yang aktif dan ramah</li>
                        <li>Lapangan badminton berkualitas di berbagai lokasi di Bandung</li>
                        <li>Coaching clinic badminton profesional</li>
                        <li>Event dan turnamen badminton rutin</li>
                    </ul>
                </Box>

                <Box sx={{ my: 4 }}>
                    <Typography variant="h5" gutterBottom sx={{ color: headingColor }}>
                        Bergabung dengan Komunitas Badminton Bandung
                    </Typography>
                    <Typography paragraph>
                        RaketerS tidak hanya sekedar platform booking mabar badminton, tapi juga wadah untuk komunitas badminton di Bandung. Bergabunglah dengan kami untuk mendapatkan informasi terbaru seputar badminton, event, dan promo menarik.
                    </Typography>
                </Box>

                <Box sx={{ my: 6 }}>
                    <Typography variant="h5" gutterBottom align="center" sx={{ color: headingColor, mb: 4 }}>
                        Orang di Balik RaketerS
                    </Typography>
                    <Typography paragraph align="center" sx={{ mb: 4, color: 'text.secondary' }}>
                        Kenali tim dedikasi di balik RaketerS yang bekerja keras untuk memberikan pengalaman badminton terbaik di Bandung. 
                        Dari pengembang platform hingga koordinator lapangan, setiap anggota tim berkontribusi untuk pertumbuhan komunitas badminton kami.
                    </Typography>
                    {loading ? (
                        <Typography align="center" sx={{ py: 4 }}>
                            Memuat data...
                        </Typography>
                    ) : people.length > 0 ? (
                        <Grid container spacing={3} justifyContent="center">
                            {/* Baris pertama: 2 orang */}
                            {people.slice(0, 2).map((person, index) => (
                                <Grid item xs={12} sm={6} md={6} key={index}>
                                    <Paper 
                                        elevation={3} 
                                        sx={{ 
                                            p: 3, 
                                            textAlign: 'center',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            background: (theme) => theme.palette.mode === 'dark' ? '#0a1626' : '#fff',
                                            border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(99,170,255,0.22)' : undefined
                                        }}
                                    >
                                        <Avatar
                                            src={person.foto}
                                            alt={person.nama}
                                            sx={{ 
                                                width: 120, 
                                                height: 120, 
                                                mb: 2,
                                                border: '3px solid',
                                                borderColor: headingColor
                                            }}
                                        />
                                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: headingColor }}>
                                            {person.nama}
                                        </Typography>
                                        {person.job && (
                                            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                                                {person.job}
                                            </Typography>
                                        )}
                                        <Chip
                                            label={person.status}
                                            color={person.status.toLowerCase() === 'aktif' ? 'success' : 'default'}
                                            size="small"
                                            sx={{ mt: 1 }}
                                        />
                                    </Paper>
                                </Grid>
                            ))}
                            {/* Baris selanjutnya: 4 orang per baris */}
                            {people.slice(2).map((person, index) => (
                                <Grid item xs={12} sm={6} md={3} key={index + 2}>
                                    <Paper 
                                        elevation={3} 
                                        sx={{ 
                                            p: 3, 
                                            textAlign: 'center',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            background: (theme) => theme.palette.mode === 'dark' ? '#0a1626' : '#fff',
                                            border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(99,170,255,0.22)' : undefined
                                        }}
                                    >
                                        <Avatar
                                            src={person.foto}
                                            alt={person.nama}
                                            sx={{ 
                                                width: 100, 
                                                height: 100, 
                                                mb: 2,
                                                border: '3px solid',
                                                borderColor: headingColor
                                            }}
                                        />
                                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: headingColor, fontSize: '1rem' }}>
                                            {person.nama}
                                        </Typography>
                                        {person.job && (
                                            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
                                                {person.job}
                                            </Typography>
                                        )}
                                        <Chip
                                            label={person.status}
                                            color={person.status.toLowerCase() === 'aktif' ? 'success' : 'default'}
                                            size="small"
                                            sx={{ mt: 1 }}
                                        />
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Typography align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            Tidak ada data tersedia
                        </Typography>
                    )}
                </Box>
            </Container>
        </>
    );
};

export default About; 